export const PageType = {
  DASHBOARD:        'dashboard',
  ORDERS:           'orders',
  ORDERS_DETAIL:    'orders-detail',
  PRODUCTS:         'products',
  PRODUCT_ADD:      'product-add',
  PRODUCT_EDIT:     'product-edit',
  PRODUCT_METADATA: 'product-metadata',
  REVIEWS:          'reviews',

  // Commerce
  CUSTOM_ORDERS:              'custom-orders',
  RETURNS_POLICY:             'returns-policy',
  SHIPPING_ZONES:              'shipping-zones',
  TENANTS:                    'tenants',
  TENANT_DETAIL:               'tenant-detail',
  MERCHANTS:                  'merchants',
  MERCHANT_DETAIL:            'merchant-detail',
  MERCHANT_APPLICATIONS:      'merchant-applications',
  STOREFRONTS:                'storefronts',
  BOOKING_SERVICES:           'booking-services',
  BRANCHES:                   'branches',
  PACKAGES:                   'packages',

  // Catalogue
  HOMEPAGE_SLIDER: 'homepage-slider',
  PORTFOLIO:       'portfolio',

  // Content
  CONTENT_PAGES:        'content-pages',
  CONTENT_PAGE_EDITOR:  'content-page-editor',

  // Finance
  MY_CONTRACT:            'my-contract',
  INVOICES:               'invoices',
  INVOICE_DETAIL:         'invoice-detail',
  PAYMENTS:               'payments',
  PAYOUTS:                'payouts',
  DISPUTES:               'disputes',
  RECONCILIATION:         'reconciliation',
  SALES_PRICING:          'sales-pricing',
  CONTACTS:               'contacts',
  CONTACT_DETAIL:         'contact-detail',
  OUTREACH:               'outreach',
  REFERRALS:              'referrals',
  REPORTING:              'reporting',
  INTELLIGENCE_REPORTS:       'intelligence-reports',
  INTELLIGENCE_REPORT_DETAIL: 'intelligence-report-detail',

  // Configuration
  CONTRACT_TEMPLATES: 'contract-templates',
  TCS_CONFIG:         'tcs-config',
  INTAKE_CATALOG:     'intake-catalog',
  FEATURE_FLAGS:      'feature-flags',
  ONBOARDING_FIELDS:  'onboarding-fields',
  INSTAGRAM_SETTINGS:            'instagram-settings',
  INSTAGRAM_CONNECTIONS:         'instagram-connections',
  INSTAGRAM_PLATFORM_CREDENTIAL: 'instagram-platform-credential',
  TEAM:               'team',

  // Charter
  CHARTER_SERVICES:        'charter-services',
  CHARTER_CATALOGUE:       'charter-catalogue',
  CHARTER_BOOKINGS:        'charter-bookings',
  CHARTER_ORDERS:          'charter-orders',
  CHARTER_SETTINGS:        'charter-settings',
  CHARTER_WAITLIST:        'charter-waitlist',
  CHARTER_PROVIDERS:       'charter-providers',
  CHARTER_PROVIDER_DETAIL: 'charter-provider-detail',
  CHARTER_OVERSIGHT:       'charter-oversight',

  // Admin
  STAFF:            'staff',
  LEADS:            'leads',
  EMAIL_CAMPAIGNS:  'email-campaigns',
  AUDIT_LOGS:       'audit-logs',
  SECURITY:         'security',
} as const;

export type PageType = (typeof PageType)[keyof typeof PageType];

export type NavParams = { type: PageType; id?: string; action?: string };

export function resolveNavRoute(params: NavParams): string | null {
  switch (params.type) {
    case PageType.DASHBOARD:
      return '/';
    case PageType.ORDERS:
      return '/orders';
    case PageType.ORDERS_DETAIL:
      return params.id ? `/orders?orderId=${encodeURIComponent(params.id)}` : '/orders';
    case PageType.PRODUCTS:
      return '/products';
    case PageType.PRODUCT_ADD:
      return '/products/add';
    case PageType.PRODUCT_EDIT:
      return params.id ? `/products/edit/${encodeURIComponent(params.id)}` : '/products';
    case PageType.PRODUCT_METADATA:
      return '/product-metadata';
    case PageType.REVIEWS:
      return '/reviews';

    case PageType.CUSTOM_ORDERS:
      return '/custom-orders';
    case PageType.RETURNS_POLICY:
      return '/returns-policy';
    case PageType.SHIPPING_ZONES:
      return '/shipping-zones';
    case PageType.TENANTS:
      return '/tenants';
    case PageType.TENANT_DETAIL:
      return params.id ? `/tenants/${encodeURIComponent(params.id)}` : '/tenants';
    case PageType.MERCHANTS:
      return '/merchants';
    case PageType.MERCHANT_DETAIL:
      return params.id ? `/merchants/${encodeURIComponent(params.id)}` : '/merchants';
    case PageType.MERCHANT_APPLICATIONS:
      return '/merchant-applications';
    case PageType.STOREFRONTS:
      return '/storefronts';
    case PageType.BOOKING_SERVICES:
      return '/booking-services';
    case PageType.BRANCHES:
      return '/branches';
    case PageType.PACKAGES:
      return '/packages';

    case PageType.HOMEPAGE_SLIDER:
      return '/homepage-slider';
    case PageType.PORTFOLIO:
      return '/portfolio';

    case PageType.CONTENT_PAGES:
      return '/content-pages';
    case PageType.CONTENT_PAGE_EDITOR:
      return params.id ? `/content-pages/${encodeURIComponent(params.id)}` : '/content-pages/new';

    case PageType.MY_CONTRACT:
      return '/my-contract';
    case PageType.INVOICES:
      return '/invoices';
    case PageType.INVOICE_DETAIL:
      return params.id ? `/invoices/${encodeURIComponent(params.id)}` : '/invoices';
    case PageType.PAYMENTS:
      return '/payments';
    case PageType.PAYOUTS:
      return '/payouts';
    case PageType.DISPUTES:
      return '/disputes';
    case PageType.RECONCILIATION:
      return '/reconciliation';
    case PageType.SALES_PRICING:
      return '/sales-pricing';
    case PageType.CONTACTS:
      return '/contacts';
    case PageType.CONTACT_DETAIL:
      return params.id ? `/contacts/${encodeURIComponent(params.id)}` : '/contacts';
    case PageType.OUTREACH:
      return '/outreach';
    case PageType.REFERRALS:
      return '/referrals';
    case PageType.REPORTING:
      return '/reporting';
    case PageType.INTELLIGENCE_REPORTS:
      return '/intelligence-reports';
    case PageType.INTELLIGENCE_REPORT_DETAIL:
      return params.id ? `/intelligence-reports/${encodeURIComponent(params.id)}` : '/intelligence-reports';

    case PageType.CONTRACT_TEMPLATES:
      return '/contract-templates';
    case PageType.TCS_CONFIG:
      return '/tcs-config';
    case PageType.INTAKE_CATALOG:
      return '/intake-catalog';
    case PageType.FEATURE_FLAGS:
      return '/feature-flags';
    case PageType.ONBOARDING_FIELDS:
      return '/onboarding-fields';
    // ⚠️ Must stay '/settings/instagram' — osprey-api's OAuth callback redirects the browser
    // to exactly this path after Meta comes back.
    case PageType.INSTAGRAM_SETTINGS:
      return '/settings/instagram';
    case PageType.INSTAGRAM_CONNECTIONS:
      return '/instagram/connections';
    case PageType.INSTAGRAM_PLATFORM_CREDENTIAL:
      return '/instagram/platform-credential';
    case PageType.TEAM:
      return '/team';

    case PageType.CHARTER_SERVICES:
      return '/charter/services';
    case PageType.CHARTER_CATALOGUE:
      return '/charter/catalogue';
    case PageType.CHARTER_BOOKINGS:
      return '/charter/bookings';
    case PageType.CHARTER_ORDERS:
      return '/charter/orders';
    case PageType.CHARTER_SETTINGS:
      return '/charter/settings';
    case PageType.CHARTER_WAITLIST:
      return '/charter/waitlist';
    case PageType.CHARTER_PROVIDERS:
      return '/charter/providers';
    case PageType.CHARTER_PROVIDER_DETAIL:
      return params.id ? `/charter/providers/${encodeURIComponent(params.id)}` : '/charter/providers';
    case PageType.CHARTER_OVERSIGHT:
      return '/charter/oversight';

    case PageType.STAFF:
      return '/staff';
    case PageType.LEADS:
      return '/leads';
    case PageType.EMAIL_CAMPAIGNS:
      return '/email-campaigns';
    case PageType.AUDIT_LOGS:
      return '/audit-logs';
    case PageType.SECURITY:
      return '/security';

    default:
      return null;
  }
}
